import { ApiError, kioskConnect } from '@/lib/api';
import { clearKioskToken, getKioskToken, setKioskToken } from '@/lib/kiosk-token';
import { Loader2, PawPrint } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export function KioskSetup() {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paired, setPaired] = useState<{
    name: string;
    facilityName: string;
  } | null>(null);

  // Already-paired branch: verify the stored token on mount.
  useEffect(() => {
    const stored = getKioskToken();
    if (!stored) return;
    // Pre-populate so the user can see what's stored.
    setToken(stored);
    setBusy(true);
    kioskConnect()
      .then((info) => {
        setPaired({ name: info.kiosk.name, facilityName: info.facilityName });
      })
      .catch(() => {
        // Stored token is invalid — clear it and let the user re-pair.
        clearKioskToken();
      })
      .finally(() => {
        setBusy(false);
      });
  }, []);

  const connect = async () => {
    setError(null);
    setBusy(true);
    setKioskToken(token.trim());
    try {
      const info = await kioskConnect();
      navigate('/checkin', { replace: true });
      // Keep the line below unreachable — navigate suspends rendering.
      // If for some reason it doesn't, record paired state.
      setPaired({ name: info.kiosk.name, facilityName: info.facilityName });
    } catch (e: unknown) {
      clearKioskToken();
      if (e instanceof ApiError && e.status === 401) {
        setError('Token invalid or revoked. Ask an admin for a fresh one.');
      } else {
        setError('Could not reach the server. Check your connection and try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const unpair = () => {
    clearKioskToken();
    setToken('');
    setPaired(null);
  };

  // Already-paired state
  if (paired) {
    return (
      <div className="grid min-h-[100dvh] place-items-center bg-background px-6">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 grid size-12 place-items-center rounded-2xl bg-primary text-primary-foreground elevate">
              <PawPrint className="size-5" strokeWidth={1.75} />
            </div>
            <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Kiosk setup
            </p>
            <h1 className="font-heading text-2xl font-medium tracking-tight">
              This device is paired
            </h1>
          </div>
          <div className="rounded-2xl bg-surface ring-1 ring-border elevate p-6 md:p-8 space-y-4">
            <div className="space-y-1">
              <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Kiosk name
              </p>
              <p className="text-base font-medium">{paired.name}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Facility
              </p>
              <p className="text-base font-medium">{paired.facilityName}</p>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => navigate('/checkin', { replace: true })}
                className="flex-1 rounded-lg bg-primary px-4 py-3 text-sm font-medium text-primary-foreground press"
              >
                Go to check-in
              </button>
              <button
                type="button"
                onClick={unpair}
                className="flex-1 rounded-lg border border-border px-4 py-3 text-sm font-medium text-foreground press"
              >
                Unpair
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid min-h-[100dvh] place-items-center bg-background px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 grid size-12 place-items-center rounded-2xl bg-primary text-primary-foreground elevate">
            <PawPrint className="size-5" strokeWidth={1.75} />
          </div>
          <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
            Kiosk setup
          </p>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Connect this tablet</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Paste the token your manager generated.
          </p>
        </div>
        <div className="rounded-2xl bg-surface ring-1 ring-border elevate p-6 md:p-8 space-y-4">
          <div>
            <label
              htmlFor="kiosk-token"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Kiosk token
            </label>
            <input
              id="kiosk-token"
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="kk_…"
              className="w-full rounded-lg border border-border bg-surface px-3 py-3 text-sm font-mono outline-none focus:ring-2 focus:ring-ring/30"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <button
            type="button"
            onClick={connect}
            disabled={busy || !token.startsWith('kk_')}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {busy && <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />}
            Pair this device
          </button>
        </div>
      </div>
    </div>
  );
}
